import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getCurrentWeekOf } from '../lib/planGenerator';

function todayISO() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

// Tonight's dinner = today's dinner_event from the current week's board plan
// (MEA-192). Read-only; returns null when there's no plan or no dinner tonight.
export function useTonight(householdId) {
  const weekOf = getCurrentWeekOf();
  const today = todayISO();
  const [event, setEvent] = useState(null);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!householdId) { setLoading(false); return; }
    setLoading(true);
    const { data: plan } = await supabase
      .from('meal_plans')
      .select('id, status')
      .eq('household_id', householdId)
      .eq('week_of', weekOf)
      .maybeSingle();
    if (!plan) { setEvent(null); setLocked(false); setLoading(false); return; }
    setLocked(plan.status === 'locked');
    const { data: ev } = await supabase
      .from('dinner_events')
      .select('*')
      .eq('meal_plan_id', plan.id)
      .eq('event_date', today)
      .maybeSingle();
    setEvent(ev || null);
    setLoading(false);
  }, [householdId, weekOf, today]);

  useEffect(() => { fetch(); }, [fetch]);

  // Per-night override for tonight's target dinner time (MEA — Tonight card).
  // hhmm is "HH:MM" (or null/'' to clear and fall back to the household default).
  // Allowed even when the week is locked — this is timing, not plan composition.
  const setTargetTime = useCallback(async (hhmm) => {
    if (!event) return;
    const { error } = await supabase
      .from('dinner_events')
      .update({ target_time: hhmm || null })
      .eq('id', event.id);
    if (error) throw error;
    await fetch();
  }, [event, fetch]);

  return { event, locked, today, weekOf, loading, refetch: fetch, setTargetTime };
}
